import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Button,
  Tooltip,
  CircularProgress,
  Chip,
  Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import StarRating from './StarRating';
import AddIcon from '@mui/icons-material/Add';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import IconButton from '@mui/material/IconButton';
import { getSecureImageUrl, handleImageError } from '../../utils/imageUtils';

/**
 * Generic grid view component for collection/wishlist items
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of items to display
 * @param {function} props.onEdit - Function to handle edit button click
 * @param {function} props.onDelete - Function to handle delete button click
 * @param {string} props.itemType - Type of item ('game', 'console', 'lego')
 * @param {boolean} props.isWishlist - Whether this is a wishlist view
 * @param {function} props.onMoveToCollection - Function to handle moving item to collection (for wishlists)
 * @param {Object} props.renderProps - Custom render props for item-specific fields
 * @returns {JSX.Element} Grid view component
 */
const GridView = ({
  items,
  onEdit,
  onDelete,
  itemType = 'item',
  isWishlist = false,
  onMoveToCollection,
  onToggleFavourite,
  onToggleBacklog,
  renderProps = {},
  cardSx = {},
}) => {
  const navigate = useNavigate();
  const { featureFlags } = useFeatureFlags();

  // Default render functions
  const defaultRenderProps = {
    getImageUrl: (item) => {
      if (itemType === 'console') {
        return item.imageUrl || item.processedImageUrl || item.platformLogo;
      }
      return item.imageUrl || item.set_img_url;
    },
    getItemName: (item) => item.name,
    getItemSubtitle: (item) => {
      if (itemType === 'game') return `Platform: ${item.platform}`;
      if (itemType === 'console') return `Generation: ${item.generation || 'N/A'}`;
      if (itemType === 'lego') return `Year: ${item.year || 'N/A'}`;
      if (itemType === 'movie') return `Format: ${item.format || 'N/A'} • Year: ${item.year || 'N/A'}`;
      if (itemType === 'music') return `${item.artist || 'Unknown Artist'} • ${item.year || 'N/A'}`;
      if (itemType === 'book') return `${item.author || 'Unknown Author'} • ${item.format || 'N/A'}`;
      if (itemType === 'board-game') {
        const players = item.minPlayers && item.maxPlayers 
          ? (item.minPlayers === item.maxPlayers ? `${item.minPlayers}p` : `${item.minPlayers}-${item.maxPlayers}p`)
          : '';
        return `${players}${players && item.playingTime ? ' • ' : ''}${item.playingTime ? `${item.playingTime} min` : ''}`;
      }
      return '';
    },
    getItemDetails: (item) => {
      if (itemType === 'game') {
        return (
          <>
            <Typography variant="body2" color="text.secondary">
              Priority: {item.priority || 'Medium'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mt: 1, rowGap: 0.5 }}>
              <Tooltip title={item.played ? 'Played' : 'Not Played'}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 0.5 }}>Played:</Typography>
                  {item.played ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                </Box>
              </Tooltip>
              <Tooltip title={item.completed ? 'Completed' : 'Not Completed'}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 0.5 }}>Completed:</Typography>
                  {item.completed ? (
                    <EmojiEventsIcon sx={{ color: 'gold' }} fontSize="small" />
                  ) : (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                </Box>
              </Tooltip>
            </Box>
          </>
        );
      }
      if (itemType === 'console') {
        return (
          <Typography variant="body2" color="text.secondary">
            Condition: {item.condition || 'N/A'}
          </Typography>
        );
      }
      if (itemType === 'lego') {
        return (
          <>
            <Typography variant="body2" color="text.secondary">
              Set #: {(item.setNum || item.set_num) ? (item.setNum || item.set_num).split('-')[0] : 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Condition: {item.condition || 'N/A'}
            </Typography>
          </>
        );
      }
      if (itemType === 'movie') {
        return (
          <>
            <Typography variant="body2" color="text.secondary">
              Director: {item.director || 'N/A'} • Condition: {item.condition || 'N/A'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Tooltip title={item.watched ? 'Watched' : 'Not Watched'}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 0.5 }}>Watched:</Typography>
                  {item.watched ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                </Box>
              </Tooltip>
            </Box>
          </>
        );
      }
      if (itemType === 'music') {
        return (
          <>
            <Typography variant="body2" color="text.secondary">
              Artist: {item.artist || 'N/A'} • Condition: {item.condition || 'N/A'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Tooltip title={item.listened ? 'Listened' : 'Not Listened'}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 0.5 }}>Listened:</Typography>
                  {item.listened ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                </Box>
              </Tooltip>
            </Box>
          </>
        );
      }
      if (itemType === 'book') {
        return (
          <>
            <Typography variant="body2" color="text.secondary">
              {item.genre || ''}{item.genre && item.year ? ' • ' : ''}{item.year || ''}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Tooltip title={item.read ? 'Read' : 'Not Read'}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 0.5 }}>Read:</Typography>
                  {item.read ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                </Box>
              </Tooltip>
            </Box>
          </>
        );
      }
      return null;
    },
    getItemDetailUrl: (item) => {
      if (itemType === 'game') return `/item/game/${item._id}`;
      if (itemType === 'console') return `/item/console/${item._id}`;
      if (itemType === 'lego') return `/item/lego/${item._id}`;
      if (itemType === 'movie') return `/item/movie/${item._id}`;
      if (itemType === 'music') return `/item/music/${item._id}`;
      if (itemType === 'book') return `/item/book/${item._id}`;
      if (itemType === 'board-game') return `/item/board-game/${item._id}`;
      if (itemType === 'trading-card') return `/item/trading-card/${item._id}`;
      if (itemType === 'manga') return `/item/manga/${item._id}`;
      if (itemType === 'comic') return `/item/comic/${item._id}`;
      return '#';
    }
  };

  // Merge default render props with custom render props
  const mergedRenderProps = { ...defaultRenderProps, ...renderProps };

  return (
    <Grid container spacing={3}>
      {items.map((item) => (
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={item._id}>
          <Card
            onClick={() => {
              const itemWithWishlistFlag = { ...item, isWishlistItem: isWishlist };
              navigate(mergedRenderProps.getItemDetailUrl(item), {
                state: { item: itemWithWishlistFlag, fromWishlist: isWishlist }
              });
            }}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'translateY(-2px)' },
              backgroundColor: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 3,
              backdropFilter: 'blur(6px)',
              cursor: 'pointer',
              ...cardSx,
            }}
          >
            {mergedRenderProps.getImageUrl(item) ? (
              <Box sx={{ pt: '56.25%', position: 'relative' }}>
                {/* Sold badge */}
                {item.isSold && (
                  <Chip
                    label="SOLD"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 6,
                      left: 6,
                      zIndex: 2,
                      bgcolor: 'warning.main',
                      color: 'warning.contrastText',
                      fontWeight: 700,
                      fontSize: '0.7rem'
                    }}
                  />
                )}
                {/* Backlog toggle button */}
                {onToggleBacklog && !isWishlist && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleBacklog(item);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      left: 4,
                      zIndex: 2,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                    }}
                  >
                    {item.inBacklog ? (
                      <BookmarkAddedIcon sx={{ color: '#ce93d8' }} fontSize="small" />
                    ) : (
                      <BookmarkAddIcon sx={{ color: 'white' }} fontSize="small" />
                    )}
                  </IconButton>
                )}
                {/* Favourite toggle button */}
                {onToggleFavourite && !isWishlist && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleFavourite(item);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      zIndex: 2,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                    }}
                  >
                    {(item.favorite || item.favourite || item.isFavorite) ? (
                      <FavoriteIcon sx={{ color: '#ff4081' }} fontSize="small" />
                    ) : (
                      <FavoriteBorderIcon sx={{ color: 'white' }} fontSize="small" />
                    )}
                  </IconButton>
                )}
                <img
                  src={getSecureImageUrl(mergedRenderProps.getImageUrl(item))}
                  alt={mergedRenderProps.getItemName(item)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    padding: '10px'
                  }}
                  loading="lazy"
                  onError={handleImageError}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  pt: '56.25%',
                  position: 'relative',
                  bgcolor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  No Image
                </Typography>
              </Box>
            )}
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="subtitle1" component="div" sx={{
                fontWeight: 600,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>
                {mergedRenderProps.getItemName(item)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {mergedRenderProps.getItemSubtitle(item)}
              </Typography>
              <Box sx={{ '&, & *': { fontSize: '0.75rem' } }}>
                {mergedRenderProps.getItemDetails(item)}
              </Box>
              {item.rating && (
                <Box sx={{ mt: 0.5 }}>
                  <StarRating value={item.rating} readOnly size="small" />
                </Box>
              )}
            </CardContent>
            {(isWishlist && onMoveToCollection) || (!isWishlist && featureFlags.enableDNF === true && item.isDNF) ? (
              <CardActions sx={{ mt: 'auto', pt: 0, px: 2, pb: 2, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                {isWishlist && onMoveToCollection && (
                  <Button
                    size="small"
                    color="success"
                    variant="contained"
                    startIcon={item.moving ? <CircularProgress size={16} /> : <LibraryAddIcon />}
                    onClick={(e) => { e.stopPropagation(); onMoveToCollection(item); }}
                    disabled={item.moving}
                  >
                    {item.moving ? 'Moving...' : 'Add to Collection'}
                  </Button>
                )}
                {!isWishlist && featureFlags.enableDNF === true && item.isDNF && (
                  <Chip
                    icon={<DoNotDisturbOnIcon />}
                    label={item.dnfPercent != null ? `DNF ${item.dnfPercent}%` : 'DNF'}
                    size="small"
                    color="warning"
                    variant="filled"
                  />
                )}
              </CardActions>
            ) : null}
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default GridView;
